import { useState, type SyntheticEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Chip, Collapse, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import MovieIcon from '@mui/icons-material/MovieFilterOutlined';
import ImageIcon from '@mui/icons-material/ImageOutlined';
import { useTranslation } from '../../i18n';
import { format } from '../../i18n/I18nProvider';
import type { EventContent, Proposal } from '../../lib/agent';
import { maxDurationFor, isH3AspectRatio } from '../../lib/h3';
import { AgentJobCard } from './AgentJobCard';
import { AgentErrorNotice } from './AgentErrorNotice';
import { contentRejection } from '../../lib/agentErrors';

const AGENT_DURATIONS = [5, 10, 15];
// Quality (base) first: it's what the agent proposes by default.
const TIERS = ['base', 'turbo'] as const;
import type { Attachment } from '../../lib/agent';

// Frames and reference images the proposal will submit (reference sheets
// included), for the thumbnail strip.
function mediaThumbs(req: Record<string, unknown>): string[] {
  const one = (v: unknown) => (typeof v === 'string' && v ? [v] : []);
  const many = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && !!x) : []);
  return [...one(req.first_frame_url), ...one(req.last_frame_url), ...many(req.reference_image_urls)];
}

export function ProposalCard({
  proposal,
  event,
  disabled,
  onConfirm,
  onCancel,
  onUseImage,
  onRevise,
}: {
  proposal: Proposal;
  // Latest settlement event for this proposal, if any.
  event?: EventContent;
  disabled: boolean;
  onConfirm: (edits: { prompt?: string; duration?: number; tier?: string }) => Promise<void>;
  onCancel: () => Promise<void>;
  onUseImage: (a: Attachment, action: 'animate' | 'edit') => void;
  // Ask the agent to revise the plan (after a content-check refusal).
  onRevise?: () => void;
}) {
  const t = useTranslation().videoAgent;
  const isVideo = proposal.kind === 'video';
  const req = proposal.request;
  const [showPrompt, setShowPrompt] = useState(false);
  const [prompt, setPrompt] = useState(String(req.prompt ?? ''));
  const [duration, setDuration] = useState<number>(Number(req.duration ?? 5));
  const [tier, setTier] = useState<string>(String(req.tier ?? 'turbo'));
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<{ raw: string; status?: number; code?: string; reason?: string } | null>(null);
  // A failed submit from this session, or one persisted before a reload.
  const shownError = error ?? (event?.type === 'proposal_failed' ? { raw: event.error, code: event.code, reason: event.reason } : null);
  // A content-check refusal can't be retried as is; the agent revises the plan.
  const contentRejected = !!shownError && !!contentRejection(shownError);
  const thumbs = isVideo ? mediaThumbs(req) : [];

  const settled = event && event.type !== 'proposal_failed';
  const cap = isVideo
    ? maxDurationFor(Number(req.short_edge ?? 768), isH3AspectRatio(req.aspect_ratio) ? req.aspect_ratio : 'auto')
    : 0;
  // Three clear choices (the agent only proposes these); cards saved before
  // this keep their original duration selectable.
  const durations = isVideo
    ? [...new Set([...AGENT_DURATIONS.filter((d) => d <= cap), Number(req.duration)])].sort((a, b) => a - b)
    : [];
  // Tier switch: only offered when the server quoted both tiers (older cards
  // keep their fixed tier).
  const tierQuotes = proposal.credits_by_tier ?? {};
  const tiers = isVideo && TIERS.every((x) => typeof tierQuotes[x] === 'number') ? TIERS : [];
  // Per-second pricing: scale the chosen tier's quote locally so the button
  // tracks the pickers; the server re-quotes and charges its own number.
  const baseQuote = tiers.length ? tierQuotes[tier as (typeof TIERS)[number]] ?? proposal.credits : proposal.credits;
  const credits = isVideo && Number(req.duration) > 0 ? Math.ceil((baseQuote / Number(req.duration)) * duration) : proposal.credits;
  const tierLabel = (x: string) => (x === 'base' ? t.tierQuality : x === 'turbo' ? t.tierTurbo : x);

  const confirm = async () => {
    setWorking(true);
    setError(null);
    try {
      await onConfirm({
        prompt: prompt.trim() !== String(req.prompt ?? '') ? prompt.trim() : undefined,
        duration: isVideo && duration !== Number(req.duration) ? duration : undefined,
        tier: isVideo && tier !== String(req.tier) ? tier : undefined,
      });
    } catch (e) {
      const err = e as Error & { code?: string; status?: number; reason?: string };
      setError({ raw: err.message, status: err.status, code: err.code, reason: err.reason });
    } finally {
      setWorking(false);
    }
  };

  const cancel = async () => {
    setWorking(true);
    try {
      await onCancel();
    } finally {
      setWorking(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, maxWidth: 560 }}>
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.25 }}>
        <Box sx={{ color: '#b9b0ff', display: 'flex' }}>{isVideo ? <MovieIcon fontSize="small" /> : <ImageIcon fontSize="small" />}</Box>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {proposal.title}
        </Typography>
        {event?.type === 'proposal_confirmed' && <Chip size="small" color="success" variant="outlined" label={t.confirmed} />}
        {event?.type === 'proposal_cancelled' && <Chip size="small" variant="outlined" label={t.dismissed} />}
      </Stack>

      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1.25 }}>
        {Object.entries(proposal.summary)
          .filter(([k]) => !(isVideo && (k === 'duration' || k === 'quality')))
          .map(([k, v]) => (
            <Chip key={k} size="small" label={String(v)} sx={{ bgcolor: 'action.selected' }} />
          ))}
        {isVideo &&
          (settled ? (
            <Chip size="small" label={`${duration}s`} sx={{ bgcolor: 'action.selected' }} />
          ) : (
            <TextField
              select
              size="small"
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              disabled={disabled || working}
              aria-label={t.duration}
              sx={{ '& .MuiSelect-select': { py: 0.25, fontSize: 13 } }}
            >
              {durations.map((d) => (
                <MenuItem key={d} value={d}>
                  {d}s
                </MenuItem>
              ))}
            </TextField>
          ))}
        {isVideo &&
          (settled || !tiers.length ? (
            <Chip size="small" label={tierLabel(tier)} sx={{ bgcolor: 'action.selected' }} />
          ) : (
            <TextField
              select
              size="small"
              value={tier}
              onChange={(e) => setTier(e.target.value)}
              disabled={disabled || working}
              aria-label={t.tier}
              sx={{ '& .MuiSelect-select': { py: 0.25, fontSize: 13 } }}
            >
              {tiers.map((x) => (
                <MenuItem key={x} value={x}>
                  {tierLabel(x)}
                </MenuItem>
              ))}
            </TextField>
          ))}
      </Stack>

      {thumbs.length > 0 && (
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1.25 }} aria-label={t.references}>
          {thumbs.map((src) => (
            <Box
              key={src}
              component="img"
              src={src}
              alt=""
              // Presigned URLs expire; an old card just drops the thumbnail.
              onError={(e: SyntheticEvent<HTMLImageElement>) => (e.currentTarget.style.display = 'none')}
              sx={{ height: 64, maxWidth: 160, objectFit: 'contain', borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}
            />
          ))}
        </Stack>
      )}

      {proposal.brief && proposal.brief !== req.prompt && (
        <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap', display: '-webkit-box', WebkitLineClamp: 6, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {proposal.brief}
        </Typography>
      )}

      <Button size="small" onClick={() => setShowPrompt((v) => !v)} sx={{ px: 0.5, minWidth: 0 }}>
        {showPrompt ? t.hidePrompt : t.editPrompt}
      </Button>
      <Collapse in={showPrompt}>
        {proposal.prompt_source === 'h3-skill' && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {t.h3SkillNote}
          </Typography>
        )}
        <TextField
          multiline
          fullWidth
          minRows={3}
          maxRows={14}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          disabled={!!settled || disabled || working}
          sx={{ mt: 1, '& textarea': { fontSize: 13 } }}
        />
      </Collapse>

      {shownError && !settled && (
        <Box sx={{ mt: 1.5 }}>
          <AgentErrorNotice
            context="submit"
            error={shownError}
            // Re-submits through the same confirm path; the server refuses a
            // second job if the first one went through.
            onRetry={contentRejected ? undefined : () => void confirm()}
            retrying={working}
            disabled={disabled || !prompt.trim()}
            action={
              contentRejected && onRevise ? (
                <Button color="inherit" size="small" variant="outlined" onClick={onRevise} disabled={disabled}>
                  {t.reviseWithAgent}
                </Button>
              ) : shownError.code === 'insufficient_credits' || /not enough credits/i.test(shownError.raw) ? (
                <Button component={RouterLink} to="/payment" color="inherit" size="small" variant="outlined">
                  {t.buyCredits}
                </Button>
              ) : undefined
            }
          />
        </Box>
      )}

      {!settled && (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Button variant="contained" onClick={confirm} disabled={disabled || working || !prompt.trim()}>
            {format(t.confirm, { credits })}
          </Button>
          <Button variant="text" onClick={cancel} disabled={disabled || working}>
            {t.cancel}
          </Button>
        </Stack>
      )}

      {event?.type === 'proposal_confirmed' && (
        <Box sx={{ mt: 1.5 }}>
          <AgentJobCard activityId={event.activity_id} kind={event.kind} onUseImage={onUseImage} />
        </Box>
      )}
    </Paper>
  );
}
